// ABOUTME: Cloudflare Workers storage provider for image upload and management via R2
// ABOUTME: Implements IStorageProvider by calling the images REST API

import type {
  IStorageProvider,
  UploadOptions,
  UploadResult,
  FileMetadata,
} from '../interfaces'

export class CloudflareStorageProvider implements IStorageProvider {
  private apiUrl: string

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl
  }

  async upload(bucket: string, path: string, file: Blob | File, _options?: UploadOptions): Promise<UploadResult> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('path', path)
    formData.append('bucket', bucket)

    try {
      const response = await fetch(`${this.apiUrl}/api/v1/images/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { path, url: '', error: new Error((errorData as { message?: string }).message || 'Upload failed') }
      }

      const data = await response.json() as { key: string; url: string }
      return { path: data.key, url: data.url }
    } catch (err) {
      return { path, url: '', error: err instanceof Error ? err : new Error('Upload failed') }
    }
  }

  async download(_bucket: string, _path: string): Promise<Blob> {
    throw new Error('Download not implemented for Cloudflare storage. Use getPublicUrl instead.')
  }

  getPublicUrl(bucket: string, path: string): string {
    return `${this.apiUrl}/${bucket}/${path}`
  }

  async delete(_bucket: string, path: string): Promise<{ error?: Error }> {
    try {
      const response = await fetch(`${this.apiUrl}/api/v1/images/${path}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { error: new Error((errorData as { message?: string }).message || 'Delete failed') }
      }

      return {}
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Delete failed') }
    }
  }

  async list(_bucket: string, _path?: string): Promise<FileMetadata[]> {
    throw new Error('List not implemented for Cloudflare storage.')
  }

  async createSignedUrl(_bucket: string, _path: string, _expiresIn: number): Promise<{ url: string; error?: Error }> {
    throw new Error('Signed URLs not implemented for Cloudflare storage.')
  }

  async getMetadata(_bucket: string, _path: string): Promise<FileMetadata | null> {
    throw new Error('getMetadata not implemented for Cloudflare storage.')
  }
}
